/**
 * /sessions-review · M06 会话复盘独立页（W9）· 脱离寄生 /debug。
 * 列会话 → 点开 → 该会话各轮 trace（复用 W4 列表 by session_id）→ 看 TraceDetail。
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { TraceDetail } from "@/components/m01/trace-detail";
import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Drawer } from "@/components/ui/drawer";
import { type SessionRecentItem, useRecentSessions } from "@/lib/api/sessions";
import { useTraceList } from "@/lib/api/trace";

export const Route = createFileRoute("/sessions-review")({
	component: SessionsReviewPage,
});

function formatTime(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return new Intl.DateTimeFormat("zh-CN", {
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	}).format(d);
}

function SessionsReviewPage() {
	const [keyword, setKeyword] = useState("");
	const [selected, setSelected] = useState<SessionRecentItem | null>(null);
	const sessions = useRecentSessions(50);

	const rows = useMemo(() => {
		const items = sessions.data?.items ?? [];
		const kw = keyword.trim().toLowerCase();
		if (!kw) return items;
		return items.filter(
			(s) =>
				s.session_id.toLowerCase().includes(kw) ||
				(s.intent ?? "").toLowerCase().includes(kw) ||
				s.merchant_id.toLowerCase().includes(kw),
		);
	}, [sessions.data?.items, keyword]);

	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5" data-testid="m06-shell">
			<PageHeader
				moduleId="M06"
				title="会话复盘"
				description="独立的会话复盘入口：按会话查看每一轮的 trace 链路，不再寄生在运行记录里。"
				help={sessionsReviewHelp}
			/>

			<Card>
				<CardHeader>
					<CardTitle className="text-lg">最近会话</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					<input
						value={keyword}
						onChange={(event) => setKeyword(event.target.value)}
						placeholder="搜索会话 ID / 意图 / 商户"
						className="h-9 w-full max-w-md rounded-md border border-surface-border bg-surface-background px-3 text-sm"
						data-testid="sessions-review-search"
					/>
					{sessions.isLoading ? (
						<div className="p-6 text-center text-sm text-text-muted">加载中…</div>
					) : rows.length === 0 ? (
						<div
							className="rounded-md border border-dashed border-surface-border p-8 text-center text-sm text-text-muted"
							data-testid="sessions-review-empty"
						>
							没有会话。
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full border-collapse text-sm" data-testid="sessions-review-table">
								<thead className="bg-surface-subtle text-left">
									<tr>
										<th className="px-3 py-2 font-medium">最近活动</th>
										<th className="px-3 py-2 font-medium">意图</th>
										<th className="px-3 py-2 font-medium">状态</th>
										<th className="px-3 py-2 font-medium">进展</th>
										<th className="px-3 py-2 font-medium" />
									</tr>
								</thead>
								<tbody>
									{rows.map((s) => (
										<tr
											key={s.session_id}
											className="border-t border-surface-border"
											data-testid={`sessions-review-row-${s.session_id}`}
										>
											<td className="px-3 py-2 font-mono text-xs">
												{formatTime(s.last_activity_time)}
											</td>
											<td className="px-3 py-2">{s.intent ?? "—"}</td>
											<td className="px-3 py-2">{s.session_status}</td>
											<td className="px-3 py-2 text-text-secondary">{s.progression_state}</td>
											<td className="px-3 py-2">
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => setSelected(s)}
													data-testid={`sessions-review-open-${s.session_id}`}
												>
													复盘
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			<Drawer
				open={selected !== null}
				onClose={() => setSelected(null)}
				title="会话复盘"
				width="min(1040px,100vw)"
				testId="sessions-review-drawer"
			>
				{selected ? <SessionReviewBody session={selected} /> : null}
			</Drawer>
		</div>
	);
}

function SessionReviewBody({ session }: { session: SessionRecentItem }) {
	const traces = useTraceList({ session_id: session.session_id, limit: 50 });
	const turns = useMemo(
		() => [...(traces.data?.items ?? [])].sort((a, b) => a.turn_index - b.turn_index),
		[traces.data?.items],
	);
	const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
	const activeTraceId = selectedTraceId ?? session.trace_id ?? turns[0]?.trace_id ?? null;

	return (
		<div className="flex flex-col gap-3 pt-2">
			<div className="grid gap-1 rounded-md bg-surface-subtle p-3 text-xs text-text-secondary">
				<div>
					会话 <span className="font-mono text-text-primary">{session.session_id}</span>
				</div>
				<div>
					商户 {session.merchant_id} · 状态 {session.session_status} · 进展{" "}
					{session.progression_state}
				</div>
			</div>

			<div>
				<div className="mb-1 text-xs text-text-muted">对话轮次（点击切换）</div>
				{traces.isLoading ? (
					<div className="text-sm text-text-muted">加载轮次…</div>
				) : turns.length === 0 ? (
					<div className="text-sm text-text-muted" data-testid="sessions-review-no-turns">
						该会话暂无 trace。
					</div>
				) : (
					<div className="flex flex-wrap gap-2" data-testid="sessions-review-turns">
						{turns.map((t) => {
							const active = t.trace_id === activeTraceId;
							return (
								<button
									key={t.trace_id}
									type="button"
									onClick={() => setSelectedTraceId(t.trace_id)}
									className={`rounded-md border px-2 py-1 text-xs ${
										active
											? "border-brand-accent bg-surface-card text-brand-accent"
											: "border-surface-border text-text-secondary hover:bg-surface-subtle"
									}`}
									data-testid={`sessions-review-turn-${t.turn_index}`}
								>
									第 {t.turn_index} 轮 · {t.final_outcome}
									{t.degraded_modules.length > 0 ? " ⚠️" : ""}
								</button>
							);
						})}
					</div>
				)}
			</div>

			{activeTraceId ? <TraceDetail traceId={activeTraceId} /> : null}
		</div>
	);
}

const sessionsReviewHelp: PageHelp = {
	title: "会话复盘说明",
	description: "M06 会话复盘从运行记录里独立出来，按会话逐轮看链路。",
	sections: [
		{
			title: "怎么用",
			items: [
				"列表选一个会话，点「复盘」。",
				"在抽屉里按轮次切换，看每一轮的完整 trace（意图/召回/生成/校验/降级）。",
				"轮次标 ⚠️ 表示该轮有降级模块。",
			],
		},
	],
};
